import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';
import { queryKeyMap } from '../../../../_utils/query-keys';
import {
  FundBusinessUpsertMutationParamsSchema,
  FundBusinessesModel,
} from '../_model/fund-business';

export const useFundBusinesses = <T = z.infer<typeof FundBusinessesModel>>({
  projectId,
  select,
  enabled = true,
}: {
  projectId: string | undefined;
  select?: (data: z.infer<typeof FundBusinessesModel>) => T;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: queryKeyMap.fundBusiness(projectId),
    queryFn: async () => {
      const fundBusinesses = await axiosGet(
        `api/projects/${projectId}/fund-businesses/`,
      );

      return FundBusinessesModel.parse(fundBusinesses);
    },
    select,
    enabled: !!projectId && enabled,
  });
};

export const useCreateFundBusiness = (projectId: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof FundBusinessUpsertMutationParamsSchema>,
    ) => {
      const payload = FundBusinessUpsertMutationParamsSchema.parse(values);
      const res = await axiosPost(
        `/api/projects/${projectId}/fund-businesses/`,
        payload,
      );
      await invalidate(queryKeyMap.fundBusiness(projectId));

      return res;
    },
  });
};

export const useDeleteFundBusiness = (projectId: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (fundBusinessId: string) => {
      const res = await axiosDelete(
        `/api/projects/${projectId}/fund-businesses/${fundBusinessId}/`,
      );
      await invalidate(queryKeyMap.fundBusiness(projectId));

      return res;
    },
  });
};

export const useUpdateFundBusiness = (projectId: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      fundBusinessId,
      values,
    }: {
      fundBusinessId: string;
      values: z.infer<typeof FundBusinessUpsertMutationParamsSchema>;
    }) => {
      const payload = FundBusinessUpsertMutationParamsSchema.parse(values);
      const res = await axiosPatch(
        `/api/projects/${projectId}/fund-businesses/${fundBusinessId}/`,
        payload,
      );
      await invalidate(queryKeyMap.fundBusiness(projectId));

      return res;
    },
  });
};
